/**
 * Widget API Service
 * Handles fetching widget settings and related data
 *
 * Note: When widget is in an iframe, the Referer header will be the iframe's URL
 * (widgets.staging.withinehr.com), not the parent page. So we use X-Parent-Origin
 * header instead, which is sent via postMessage from the loader script.
 */

import type {
  WidgetSettingsResponse,
  AvailabilityCheckRequest,
  AvailabilityCheckResponse,
  AppointmentRequest,
  AppointmentRequestResponse,
  AppointmentRequestError,
} from '@/types';
import { getParentOrigin } from '@/utils/parentOrigin';

/**
 * Base API URL - can be configured via environment variable
 *
 * Options:
 * 1. Use proxy (empty string) - requests go through Vite dev server proxy
 * 2. Use direct URL - bypass proxy and call API directly
 *
 * Set VITE_USE_DIRECT_API=true to bypass proxy and call API directly
 */
const USE_DIRECT_API =
  import.meta.env.VITE_USE_DIRECT_API === 'true' || import.meta.env.VITE_USE_DIRECT_API === '1';

const getApiBaseUrl = (): string => {
  if (import.meta.env.PROD) {
    return (
      import.meta.env.VITE_API_BASE_URL ||
      import.meta.env.VITE_API_URL ||
      'https://api.staging.withinehr.com'
    );
  }
  if (USE_DIRECT_API) {
    return (
      import.meta.env.VITE_API_BASE_URL ||
      import.meta.env.VITE_API_URL ||
      'https://api.staging.withinehr.com'
    );
  }
  return ''; // Use relative paths in dev (will be proxied)
};

const BASE_URL = getApiBaseUrl();

/**
 * Fetch widget settings for a practice
 */
export async function fetchWidgetSettings(practiceSlug: string): Promise<WidgetSettingsResponse> {
  const url = `${BASE_URL}/api/v1/public/${practiceSlug}/widget_settings`;
  const parentOrigin = getParentOrigin();

  try {
    console.log('🌐 [WidgetAPI] ============================================');
    console.log('🌐 [WidgetAPI] Fetching widget settings');
    console.log('🌐 [WidgetAPI] Practice slug:', practiceSlug);
    console.log('🌐 [WidgetAPI] Base URL:', BASE_URL || '(empty - using proxy)');
    console.log('🌐 [WidgetAPI] Full URL:', url);
    console.log('🌐 [WidgetAPI] Environment:', {
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD,
      USE_DIRECT_API: USE_DIRECT_API,
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      VITE_API_URL: import.meta.env.VITE_API_URL,
    });
    console.log(
      '🌐 [WidgetAPI] Using proxy:',
      !USE_DIRECT_API && import.meta.env.DEV ? 'YES (via Vite proxy)' : 'NO (direct)',
    );
    // Use console.warn for important logs (persists in production)
    console.warn('🌐 [WidgetAPI] Parent origin:', parentOrigin || 'not detected');
    console.warn('🌐 [WidgetAPI] ============================================');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Send parent origin for backend domain validation
    // Note: Referer header will be widgets.staging.withinehr.com (iframe's URL),
    // not the parent page, so we need X-Parent-Origin
    if (parentOrigin) {
      headers['X-Parent-Origin'] = parentOrigin;
      // Use console.warn so it persists in production
      console.warn('🌐 [WidgetAPI] ✅ Sending X-Parent-Origin header:', parentOrigin);
      // Expose to window for debugging
      if (typeof window !== 'undefined') {
        (window as any).__WIDGET_DEBUG_X_PARENT_ORIGIN__ = parentOrigin;
      }
    } else {
      console.warn(
        '🌐 [WidgetAPI] ⚠️ WARNING: No parent origin detected - X-Parent-Origin header will NOT be sent',
      );
      console.warn('🌐 [WidgetAPI] Backend may reject this request if X-Parent-Origin is required');
      if (typeof window !== 'undefined') {
        (window as any).__WIDGET_DEBUG_X_PARENT_ORIGIN__ = null;
      }
    }

    // Log headers (use warn for production visibility)
    console.warn('🌐 [WidgetAPI] Request headers:', {
      'Content-Type': headers['Content-Type'],
      'X-Parent-Origin': headers['X-Parent-Origin'] || '(not set)',
      Origin: '(browser will send automatically: widgets.staging.withinehr.com)',
    });

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    console.log('🌐 [WidgetAPI] Response status:', response.status, response.statusText);
    console.log('🌐 [WidgetAPI] Response headers:', Object.fromEntries(response.headers.entries()));

    // Check Content-Type before parsing
    const contentType = response.headers.get('content-type') || '';
    const isHTML = contentType.includes('text/html') || contentType.includes('text/plain');

    // Get response body as text first (to check if it's HTML)
    const responseText = await response.text();

    // Check if response is HTML (likely an error page)
    if (isHTML || responseText.trim().startsWith('<!')) {
      console.error('❌ [WidgetAPI] Received HTML response instead of JSON');
      console.error('❌ [WidgetAPI] Response preview:', responseText.substring(0, 500));

      if (!response.ok) {
        throw new Error(
          `API returned HTML error page (${response.status} ${response.statusText}). ` +
            `This usually means the endpoint doesn't exist or the server returned an error page. ` +
            `URL: ${url}`,
        );
      } else {
        throw new Error(
          `API returned HTML instead of JSON. This usually means the endpoint is incorrect. ` +
            `URL: ${url}`,
        );
      }
    }

    if (!response.ok) {
      // Try to parse as JSON for error details
      let errorBody = responseText;
      let errorMessage = response.statusText;

      try {
        const errorJson = JSON.parse(responseText);
        if (errorJson.message || errorJson.error) {
          errorMessage = errorJson.message || errorJson.error;
        }
        if (errorJson.status?.message) {
          errorMessage = errorJson.status.message;
        }
        errorBody = JSON.stringify(errorJson, null, 2);
      } catch (e) {
        // Not JSON, use text as-is
      }

      console.error('❌ [WidgetAPI] Error response:', errorBody);
      throw new Error(`Failed to fetch widget settings (${response.status}): ${errorMessage}`);
    }

    // Parse JSON response
    let rawData;
    try {
      rawData = JSON.parse(responseText);
    } catch (e) {
      console.error('❌ [WidgetAPI] Failed to parse JSON response');
      console.error('❌ [WidgetAPI] Response text:', responseText.substring(0, 500));
      throw new Error(
        `Invalid JSON response from API. ` +
          `Expected JSON but received: ${contentType || 'unknown content type'}. ` +
          `URL: ${url}`,
      );
    }
    console.log('🌐 [WidgetAPI] Raw API response:', JSON.stringify(rawData, null, 2));

    // Validate response structure
    if (!rawData.data || !rawData.status) {
      console.error('❌ [WidgetAPI] Invalid response structure:', rawData);
      throw new Error('Invalid response structure from widget settings API');
    }

    // Check if response has the expected structure (full response)
    const hasFullStructure =
      rawData.data.widget_settings && rawData.data.practice && Array.isArray(rawData.data.services);

    if (hasFullStructure) {
      // Full response structure - return as-is
      console.log('✅ [WidgetAPI] Full response structure received');
      return rawData as WidgetSettingsResponse;
    }

    // Simplified response structure - transform it
    console.warn('⚠️ [WidgetAPI] Simplified response structure detected - transforming...');
    console.log('⚠️ [WidgetAPI] Response keys:', Object.keys(rawData.data));

    // Transform simplified response to expected structure
    const transformedData: WidgetSettingsResponse = {
      status: rawData.status,
      data: {
        widget_settings: {
          enabled: rawData.data.enabled ?? true,
          title: 'Book Your Appointment',
          description: 'Schedule your visit in just a few simple steps',
          primary_color: rawData.data.config?.primary_color || '#014952',
          button_text: 'Book Appointment',
          captcha_enabled: rawData.data.config?.captcha_enabled ?? false,
        },
        practice: {
          id: rawData.data.practice_id || '',
          name: rawData.data.practice_name || '',
          slug: rawData.data.practice_slug || practiceSlug,
          time_zone: rawData.data.time_zone || 'America/New_York',
        },
        services: rawData.data.services || [],
        clinicians: rawData.data.clinicians || [],
        locations: rawData.data.locations || [],
        payment_methods: rawData.data.payment_methods || [],
      },
    };

    console.log('✅ [WidgetAPI] Transformed response:', transformedData);
    return transformedData;
  } catch (error) {
    console.error('❌ [WidgetAPI] Error fetching widget settings:', error);
    throw error;
  }
}

/**
 * Check availability for appointment slots
 */
export async function checkAvailability(
  practiceSlug: string,
  request: Omit<AvailabilityCheckRequest, 'captcha_token'>,
  captchaToken?: string,
): Promise<AvailabilityCheckResponse> {
  const url = `${BASE_URL}/api/v1/public/${practiceSlug}/appointment_requests/check_availability`;
  const parentOrigin = getParentOrigin();

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Send parent origin for backend domain validation
    if (parentOrigin) {
      headers['X-Parent-Origin'] = parentOrigin;
    }

    // Wrap request in availability_check object as per API spec
    const requestBody = {
      availability_check: request.availability_check,
      ...(captchaToken && { captcha_token: captchaToken }),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      // Try to parse error response
      let errorData: AppointmentRequestError;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          success: false,
          error: `Failed to check availability: ${response.statusText}`,
          code: response.status === 404 ? 'PRACTICE_NOT_FOUND' : undefined,
        };
      }

      const error = new Error(
        errorData.error || `Failed to check availability: ${response.statusText}`,
      );
      (error as any).code = errorData.code;
      (error as any).errors = errorData.errors;
      (error as any).practice = errorData.practice;
      throw error;
    }

    const data: AvailabilityCheckResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking availability:', error);
    throw error;
  }
}

/**
 * Create an appointment request
 */
export async function createAppointmentRequest(
  practiceSlug: string,
  request: Omit<AppointmentRequest, 'captcha_token'>,
  captchaToken?: string,
): Promise<AppointmentRequestResponse> {
  const url = `${BASE_URL}/api/v1/public/${practiceSlug}/appointment_requests`;
  const parentOrigin = getParentOrigin();

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Send parent origin for backend domain validation
    if (parentOrigin) {
      headers['X-Parent-Origin'] = parentOrigin;
    }

    // Wrap request in appointment_request object as per API spec
    const requestBody = {
      appointment_request: request.appointment_request,
      ...(captchaToken && { captcha_token: captchaToken }),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      // Try to parse error response
      let errorData: AppointmentRequestError;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          success: false,
          error: `Failed to create appointment request: ${response.statusText}`,
          code: response.status === 404 ? 'PRACTICE_NOT_FOUND' : undefined,
        };
      }

      const error = new Error(
        errorData.error || `Failed to create appointment request: ${response.statusText}`,
      );
      (error as any).code = errorData.code;
      (error as any).errors = errorData.errors;
      (error as any).practice = errorData.practice;
      throw error;
    }

    const data: AppointmentRequestResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating appointment request:', error);
    throw error;
  }
}
